import { useEffect, useRef, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { WifiSlash, ArrowClockwise, SignOut } from "@phosphor-icons/react";
import "./ProtectedRoute.css";
import {
  auth,
  getTherapistWithClinic,
  recordTherapistLogin,
  subscribeToClinicDocument,
  subscribeToTherapistDocument,
  updateClinicDocument,
} from "../../firebase";
import { getIsraelDateString } from "../../utils/dateUtils";
import {
  setUser,
  setTherapistProfile,
  setClinicProfile,
  setLoading,
  setNivAdmin,
  PENDING_LOGIN_ERROR_KEY,
} from "../../store/slices/authSlice";
import { initTelemetry, resetTelemetry } from "../../services/telemetry";
import { useToast } from "../../contexts/ToastContext";

// Retry delay in milliseconds (backs off on repeated failures, capped at MAX)
const RETRY_DELAY = 500;
const RETRY_DELAY_MAX = 5000;

// After this many consecutive *thrown* profile-fetch errors (e.g. Firestore
// "client is offline" — blocked by AV / proxy / flaky network) we stop hiding
// behind the loader and show a recoverable error screen with retry + logout.
// "No data yet" (null) is NOT counted — that is expected mid-registration.
// The retry loop keeps running behind the screen so it self-heals on success.
const CONNECTION_ERROR_THRESHOLD = 3;

// "No data yet" (therapist doc missing) is expected only mid-registration on
// /signup, where the Auth user is created a few seconds before the docs. On any
// other route a signed-in Auth user with no therapist doc is an orphan (e.g. the
// Auth user was deleted from the console, then phone login re-created it under a
// new uid). Retrying forever traps them behind the loader — after this many
// consecutive null results we sign out with a Hebrew explanation (CK-1008).
const MISSING_PROFILE_THRESHOLD = 6; // ≈ 20s with the backoff below

const isOnRegistrationRoute = () =>
  typeof window !== "undefined" && window.location.pathname.startsWith("/signup");

const isOfflineError = (error) =>
  error?.code === "unavailable" ||
  /offline|network|unavailable|failed to fetch/i.test(error?.message || "");

// ============================================
// ACTIVITY TRACKING
// Updates the clinic's updatedAt field in Firestore at most once every 30 minutes.
// Uses localStorage to persist the throttle timer across page refreshes.
// ============================================
const ACTIVITY_UPDATE_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // Check every 1 minute

/**
 * Record the therapist's login at most once per Israel calendar day.
 *
 * Feeds NiV's "last login / days inactive" reporting, which used to run on a
 * lastLoginDate written only at registration. Fire-and-forget: a failure here
 * must never affect sign-in.
 */
const recordLoginIfNeeded = async (therapistId) => {
  if (!therapistId) return;

  const storageKey = `ck_lastLoginPing_${therapistId}`;
  const today = getIsraelDateString(new Date());

  try {
    if (localStorage.getItem(storageKey) === today) return;
  } catch {
    // Private mode / blocked storage — fall through and just write.
  }

  try {
    await recordTherapistLogin(therapistId);
    localStorage.setItem(storageKey, today);
  } catch (error) {
    console.error("[CK-1009] Error recording therapist login:", {
      errorCode: "CK-1009",
      errorMessage: error.message,
      timestamp: new Date().toISOString(),
    });
  }
};

/**
 * Update the clinic's updatedAt in Firestore if at least 30 minutes
 * have passed since the last update (tracked via localStorage).
 */
const updateLastActivityIfNeeded = async (clinicId) => {
  if (!clinicId) return;

  const storageKey = `ck_lastActivity_${clinicId}`;
  const lastUpdate = localStorage.getItem(storageKey);
  const now = Date.now();

  if (lastUpdate && now - parseInt(lastUpdate, 10) < ACTIVITY_UPDATE_INTERVAL_MS) {
    return; // Not enough time has passed
  }

  try {
    await updateClinicDocument(clinicId, {}); // Only touches updatedAt via serverTimestamp()
    localStorage.setItem(storageKey, now.toString());
  } catch (error) {
    console.error("[CK-AUTH] Error updating clinic activity:", error.message);
  }
};

// Preload an image and return a promise
const preloadImage = (url) => {
  return new Promise((resolve) => {
    if (!url) {
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve(); // Resolve even on error to not block
    img.src = url;
  });
};

function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const store = useStore();
  const retryTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const activityIntervalRef = useRef(null);
  const clinicListenerRef = useRef(null);
  const therapistListenerRef = useRef(null);
  const { showError } = useToast();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  // Recoverable connection-error screen state (see CONNECTION_ERROR_THRESHOLD)
  const [connectionError, setConnectionError] = useState(null); // null | { offline: boolean }
  const [isRetrying, setIsRetrying] = useState(false);
  const failureCountRef = useRef(0);
  const missingProfileCountRef = useRef(0); // consecutive "no therapist doc" results
  const attemptFetchRef = useRef(null); // current retry-loop attempt fn, for manual/online retry

  // Hand off from the static pre-paint boot overlay (index.html) to React's own
  // loaders. By the time this effect runs the tree has committed, so AuthRedirect /
  // ProtectedRoute are already showing their loaders — removing the static overlay
  // reveals them with no landing-page flash in between.
  useEffect(() => {
    document.documentElement.removeAttribute("data-ck-auth-boot");
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    // Check if profile is already loaded (e.g., set by VerificationStep after registration)
    const isProfileAlreadyLoaded = () => {
      const state = store.getState();
      return state.auth.therapistProfile !== null && !state.auth.isLoading;
    };

    // A deactivated therapist (isActive === false) can read their own doc but
    // every clinic-scoped read is permission-denied. Without this the retry
    // loop spins forever behind the loader. Sign out and explain in Hebrew.
    const signOutDeactivated = async (userId, reason) => {
      console.warn("[CK-1005] Signing out: therapist has no clinic access", {
        errorCode: "CK-1005",
        reason,
        userId,
        timestamp: new Date().toISOString(),
      });
      try {
        showErrorRef.current(
          "החשבון שלך אינו פעיל בקליניקה. פנה למנהל הקליניקה."
        );
      } catch (e) {}
      try {
        await signOut(auth);
      } catch (e) {}
      // onAuthStateChanged(null) resets the store; belt-and-braces if it doesn't fire
      if (isMountedRef.current) dispatch(setLoading(false));
      return true; // stop the retry loop
    };

    // Signed-in Auth user, no therapist doc, not on /signup → orphan account.
    // Sign out and send them to /login with an explanation (stashed so it
    // survives the redirect). We do NOT delete the Auth user here — that is only
    // safe right after a fresh phone sign-in (LoginPage handles that case).
    const signOutMissingProfile = async (userId) => {
      console.error("[CK-1008] Signing out: Auth user has no therapist profile", {
        errorCode: "CK-1008",
        attempts: missingProfileCountRef.current,
        route: typeof window !== "undefined" ? window.location.pathname : null,
        userId,
        timestamp: new Date().toISOString(),
      });
      const message =
        "לא נמצא פרופיל משתמש עבור חשבון זה. אנא הירשמו מחדש או פנו לתמיכה.";
      try {
        sessionStorage.setItem(PENDING_LOGIN_ERROR_KEY, message);
      } catch (e) {}
      try {
        localStorage.removeItem("ck_authed");
      } catch (e) {}
      try {
        showErrorRef.current(message);
      } catch (e) {}
      try {
        await signOut(auth);
      } catch (e) {}
      if (isMountedRef.current) dispatch(setLoading(false));
      return true; // stop the retry loop
    };

    // Function to fetch profile with retry logic
    const fetchProfileWithRetry = async (userId) => {
      // Skip if profile was already set (e.g., by registration flow)
      if (isProfileAlreadyLoaded()) {
        return true;
      }

      try {
        const data = await getTherapistWithClinic(userId);
        // Network round-trip succeeded (even if doc missing) — reset error state
        failureCountRef.current = 0;
        if (isMountedRef.current) setConnectionError(null);
        if (data && data.therapist && data.therapist.isActive === false) {
          return signOutDeactivated(userId, "isActive=false");
        }
        if (data && data.therapist) {
          missingProfileCountRef.current = 0;
          // Check again in case profile was set while we were fetching
          if (isProfileAlreadyLoaded()) {
            return true;
          }

          // Success - set profiles
          if (isMountedRef.current) {
            dispatch(setTherapistProfile(data.therapist));
            dispatch(setClinicProfile(data.clinic));
            
            // Preload clinic logo if exists before finishing loading
            if (data.clinic?.logoUrl) {
              await preloadImage(data.clinic.logoUrl);
            }
            
            // Now we can stop loading
            if (isMountedRef.current) {
              dispatch(setLoading(false));
            }

            // Login ping (fire-and-forget). Outside the clinic guard so a
            // therapist still mid-registration is counted too.
            recordLoginIfNeeded(data.therapist?.uid);

            // Track clinic activity (fire-and-forget, does not block UI)
            if (data.clinic?.id) {
              initTelemetry({
                clinicId: data.clinic.id,
                clinicName: data.clinic.name || "",
                therapistId: data.therapist.uid,
                therapistName: data.therapist.fullName || "",
              });

              updateLastActivityIfNeeded(data.clinic.id);

              // Set up periodic activity check while user is active
              if (activityIntervalRef.current) {
                clearInterval(activityIntervalRef.current);
              }
              activityIntervalRef.current = setInterval(() => {
                if (isMountedRef.current) {
                  const state = store.getState();
                  const clinicId = state.auth.clinicProfile?.id;
                  if (clinicId) {
                    updateLastActivityIfNeeded(clinicId);
                  }
                }
              }, ACTIVITY_CHECK_INTERVAL_MS);
            }

            // Live therapist profile updates (role / per-therapist permissions
            // changed by an admin take effect within seconds — no re-login).
            // Keeps the shape getTherapistWithClinic produced (uid preserved).
            if (therapistListenerRef.current) {
              therapistListenerRef.current();
            }
            therapistListenerRef.current = subscribeToTherapistDocument(
              userId,
              (therapistData) => {
                // Admin deactivated this account while it was signed in: every
                // clinic-scoped read is about to be permission-denied, so end
                // the session now instead of leaving a half-dead dashboard.
                if (therapistData && therapistData.isActive === false) {
                  signOutDeactivated(userId, "live-isActive=false");
                  return;
                }
                if (isMountedRef.current) {
                  dispatch(
                    setTherapistProfile({
                      ...data.therapist,
                      ...therapistData,
                      uid: therapistData.uid || data.therapist?.uid || userId,
                    })
                  );
                }
              }
            );

            // Live clinic profile updates (plan changes, admin deactivation)
            const clinicId = data.therapist?.clinicId || data.clinic?.id;
            if (clinicId) {
              if (clinicListenerRef.current) {
                clinicListenerRef.current();
              }
              clinicListenerRef.current = subscribeToClinicDocument(
                clinicId,
                (clinicData) => {
                  if (isMountedRef.current) {
                    dispatch(setClinicProfile(clinicData));
                  }
                }
              );
            }
          }
          return true;
        } else {
          // No therapist doc. Expected mid-registration (docs written seconds
          // after the Auth user); an orphan account anywhere else.
          missingProfileCountRef.current += 1;
          if (
            !isOnRegistrationRoute() &&
            missingProfileCountRef.current >= MISSING_PROFILE_THRESHOLD
          ) {
            return signOutMissingProfile(userId);
          }
          return false;
        }
      } catch (error) {
        console.error("[CK-1004] Error fetching therapist profile:", {
          errorCode: "CK-1004",
          errorMessage: error.message,
          userId: userId,
          timestamp: new Date().toISOString(),
        });
        if (error?.code === "permission-denied") {
          return signOutDeactivated(userId, "permission-denied");
        }
        failureCountRef.current += 1;
        if (
          failureCountRef.current >= CONNECTION_ERROR_THRESHOLD &&
          isMountedRef.current
        ) {
          const offline = isOfflineError(error);
          if (failureCountRef.current === CONNECTION_ERROR_THRESHOLD) {
            console.error("[CK-1007] Profile fetch failing repeatedly — showing connection error screen:", {
              errorCode: "CK-1007",
              errorMessage: error.message,
              firestoreCode: error?.code || null,
              offline,
              attempts: failureCountRef.current,
              userId,
              timestamp: new Date().toISOString(),
            });
          }
          setConnectionError({ offline });
        }
        return false;
      }
    };

    // Function to start retry loop.
    // "No data yet" is expected mid-registration (therapist doc not written
    // yet), so the loop is unbounded — but it backs off to RETRY_DELAY_MAX so a
    // transient outage doesn't hammer Firestore twice a second.
    const startRetryLoop = (userId) => {
      let attempt = 0;
      let inFlight = false;
      const attemptFetch = async () => {
        if (!isMountedRef.current) return;
        if (inFlight) return; // manual/online retry while a fetch is pending — ignore

        // Skip if profile was already set
        if (isProfileAlreadyLoaded()) {
          return;
        }

        // Cancel any scheduled retry — we're attempting now
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }

        inFlight = true;
        let success = false;
        try {
          success = await fetchProfileWithRetry(userId);
        } finally {
          inFlight = false;
        }
        if (!success && isMountedRef.current) {
          attempt += 1;
          const delay = Math.min(RETRY_DELAY * 2 ** Math.min(attempt, 10), RETRY_DELAY_MAX);
          retryTimeoutRef.current = setTimeout(attemptFetch, delay);
        }
      };

      attemptFetchRef.current = attemptFetch;
      attemptFetch();
    };

    // Browser regained connectivity — retry immediately instead of waiting for backoff
    const handleOnline = () => {
      if (attemptFetchRef.current) attemptFetchRef.current();
    };
    window.addEventListener("online", handleOnline);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Clear any pending retry
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      attemptFetchRef.current = null;
      failureCountRef.current = 0;
      missingProfileCountRef.current = 0;
      setConnectionError(null);

      if (user) {
        // User is signed in — persist a hint so the pre-paint boot guard in
        // index.html can show a loader (instead of flashing the landing page)
        // next cold start before Firebase auth resolves.
        try {
          localStorage.setItem("ck_authed", "1");
        } catch (e) {}

        dispatch(
          setUser({
            uid: user.uid,
            phoneNumber: user.phoneNumber,
            email: user.email,
          })
        );

        // Check custom claims for NiV admin status
        try {
          const tokenResult = await user.getIdTokenResult();
          dispatch(setNivAdmin(tokenResult.claims.nivAdmin === true));
        } catch (claimsError) {
          console.error("[CK-AUTH] Error fetching custom claims:", claimsError);
          dispatch(setNivAdmin(false));
        }

        // Start fetching profile with retry logic
        startRetryLoop(user.uid);
      } else {
        // User is signed out — flush + stop telemetry
        resetTelemetry();

        // Clear activity tracking interval
        if (activityIntervalRef.current) {
          clearInterval(activityIntervalRef.current);
          activityIntervalRef.current = null;
        }

        // Stop listening to clinic document changes
        if (clinicListenerRef.current) {
          clinicListenerRef.current();
          clinicListenerRef.current = null;
        }
        if (therapistListenerRef.current) {
          therapistListenerRef.current();
          therapistListenerRef.current = null;
        }

        // Clear the boot hint so a logged-out cold start shows the landing
        // page directly without flashing a loader.
        try {
          localStorage.removeItem("ck_authed");
        } catch (e) {}

        dispatch(setUser(null));
        dispatch(setTherapistProfile(null));
        dispatch(setClinicProfile(null));
        dispatch(setNivAdmin(false));
        dispatch(setLoading(false));
      }
    });

    // Cleanup subscription, retry timeout, and activity interval
    return () => {
      isMountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
      }
      if (clinicListenerRef.current) {
        clinicListenerRef.current();
      }
      if (therapistListenerRef.current) therapistListenerRef.current();
      window.removeEventListener("online", handleOnline);
      unsubscribe();
    };
  }, [dispatch, store]);

  const handleManualRetry = async () => {
    if (isRetrying || !attemptFetchRef.current) return;
    setIsRetrying(true);
    try {
      await attemptFetchRef.current();
    } finally {
      if (isMountedRef.current) setIsRetrying(false);
    }
  };

  const handleLogout = async () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    attemptFetchRef.current = null;
    try {
      localStorage.removeItem("ck_authed");
    } catch (e) {}
    try {
      await signOut(auth);
    } catch (e) {
      // signOut can fail while offline; still reset local state so the user
      // lands on the login page instead of staying stuck.
      console.error("[CK-1007] signOut failed from connection error screen:", {
        errorCode: "CK-1007",
        errorMessage: e?.message,
        timestamp: new Date().toISOString(),
      });
    }
    setConnectionError(null);
    dispatch(setUser(null));
    dispatch(setTherapistProfile(null));
    dispatch(setClinicProfile(null));
    dispatch(setLoading(false));
  };

  if (connectionError) {
    const offline = connectionError.offline;
    return (
      <div className="loading-screen error-screen" dir="rtl">
        <div className="error-icon">
          <WifiSlash size={56} weight="duotone" />
        </div>
        <h2>{offline ? "אין חיבור לשרת" : "שגיאה בטעינת הפרופיל"}</h2>
        <p>
          {offline
            ? "לא הצלחנו להתחבר לשרתי CliniKit. ייתכן שחיבור האינטרנט אינו יציב, או שאנטי-וירוס, VPN או תוסף בדפדפן חוסמים את החיבור."
            : "אירעה שגיאה בטעינת פרטי המשתמש. ניתן לנסות שוב או להתנתק ולהתחבר מחדש."}
        </p>
        <p className="error-hint">
          המערכת ממשיכה לנסות להתחבר ברקע. אם הבעיה נמשכת, נסו לפתוח בחלון גלישה בסתר או ברשת אחרת.
        </p>
        <div className="error-actions">
          <button
            type="button"
            className="retry-btn"
            onClick={handleManualRetry}
            disabled={isRetrying}
          >
            <ArrowClockwise size={18} weight="bold" className={isRetrying ? "spinning" : ""} />
            {isRetrying ? "מנסה..." : "נסה שוב"}
          </button>
          <button type="button" className="logout-btn" onClick={handleLogout}>
            <SignOut size={18} weight="bold" />
            התנתקות
          </button>
        </div>
      </div>
    );
  }

  return children;
}

export default AuthProvider;
